import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { FormikProvider, Form, useFormik } from 'formik';
import {
  BubbleAlert,
  BubbleButton,
  BubbleFormGroup,
  BubbleInput,
  BubbleSelect,
  BubbleTextArea,
} from '../BubbleUI';
import { BareContentTemplate } from '@go-shared/types/contentTemplate';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import { ObjectID } from 'bson';
import { createPortal } from 'react-dom';
import { Time } from '@go-shared/types/time';
import { Content } from '@go-shared/types/content';
import { addMinutesToTime, calculateTimeDifference } from '@go-shared/lib/time';
import { DEFAULT_DAY_START } from '@go-shared/lib/property';
import { Plan } from '@go-shared/types/plan';
import { EventFormDayTransfer } from '../CanvasEventForm/CanvasEventForm';
import { BareVenueTemplate, VenueTemplate } from '@go-shared/types/venueTemplate';
import { mapBy } from '@go-shared/lib/functional';
import { CallToActionsInstance } from '@go-shared/types/dailyProgram';
import { captureBetweenDollars } from '@go-shared/lib/utils';
import { useServerInfo } from '@go-client/context/ServerInfoContext';
import { ContentTemplateTranslation } from '@go-shared/types/translation';
import {
  isNotBasicContentTemplate,
  translateBareContentTemplate,
} from '@go-shared/lib/contentTemplate';
import { MergedVariables } from '@go-shared/types/mergedVars';
import { generateReplaceDict, generateTranslDict } from '@go-shared/lib/mergedVars';
import { Language } from '@go-shared/types/info';
import McmVisibilityTable from '../McmVisibilityTable';
import AudienceVisibilityTable from '../AudienceVisibilityTable';
import { Audience } from '@go-shared/types/audience';
import { Mcm } from '@go-shared/types/mcm';
import { CallToActionsManagement } from '@go-client/components/BasicForms/CallToActionsManagement';

export interface ContentFormikForm {
  startTime: Time;
  endTime: Time;
  callToActions: CallToActionsInstance;
  venue?: string;
  mergedVars: MergedVariables;
  mcm?: Mcm;
  audience?: Audience;
}

export interface ContentFormikExtState {
  values: ContentFormikForm;
  disabled: boolean;
}

function GENERATE_INITIAL_VALUES(
  dayStart: Time,
  mergedVarKeys: string[],
  languages: Language[],
  edit?: Content
): ContentFormikForm {
  const startTime = edit?.startTime || dayStart;
  const endTime = edit?.duration
    ? addMinutesToTime(startTime, edit.duration)
    : addMinutesToTime(startTime, 1439);

  return {
    startTime,
    endTime,
    callToActions: edit?.callToActions ?? {
      syncWithTemplate: true,
      values: edit?.template.callToActions || [],
    },
    venue: edit?.venue,
    mcm: edit?.mcm,
    audience: edit?.audience,
    mergedVars: {
      replacements: generateReplaceDict(mergedVarKeys, edit?.mergedVars),
      translations: generateTranslDict(
        mergedVarKeys,
        languages.map(l => l.key),
        edit?.mergedVars
      ),
    },
  };
}

export interface ContentFormikMode {
  type: 'create' | 'edit' | 'view';
  edit?: Content;
  dayTransfer?: EventFormDayTransfer;
}

export interface CntTmplFormikExtState {
  values: ContentFormikForm;
  disabled: boolean;
}

interface ContentFormikPropsBase {
  mode: ContentFormikMode;
  template: BareContentTemplate;
  plan: Plan;
  venueTemplates: Array<BareVenueTemplate | VenueTemplate>;
  contentTemplateTranslations: ContentTemplateTranslation[];
  actionsElem?: HTMLDivElement;
  actionsClassName?: string;
  onSubmit?: (content: Content) => void;
  onStateUpdate?: (state: CntTmplFormikExtState) => void;
}

export interface ContentFormikRef {
  reset: () => void;
  submit: () => void;
}

type ContentDetailsPageTab = 'basic' | 'mergedVars';
type ContentFormikProps = ContentFormikPropsBase;

// SUBMISSION IS PASSED TO THIS ONE BECAUSE IT DEPENDS ON THE CONTEXT. IS IT TEMPLATE OR DAY?
function ContentFormik(props: ContentFormikProps, ref: React.ForwardedRef<ContentFormikRef>) {
  const { languages, defaultLanguage } = useServerInfo();
  const { activeProperty: property } = useActiveProperty();

  const dayStart = property.dayStart || DEFAULT_DAY_START;
  const [activeTab, setActiveTab] = useState<ContentDetailsPageTab>('basic');

  const mergedVarKeys = isNotBasicContentTemplate(props.template)
    ? captureBetweenDollars(props.template.name)
        .concat(captureBetweenDollars(props.template.description))
        .map(m => m[1])
    : [];

  const formik = useFormik({
    initialValues: GENERATE_INITIAL_VALUES(dayStart, mergedVarKeys, languages, props.mode.edit),
    onSubmit: onFormSubmit,
  });

  useEffect(() => {
    formik.resetForm({
      values: GENERATE_INITIAL_VALUES(dayStart, mergedVarKeys, languages, props.mode.edit),
    });
  }, [props.mode.edit]);

  const { values, errors, touched, setFieldValue, handleSubmit, handleBlur } = formik;

  const actionClasses = [];
  if (props.actionsClassName) actionClasses.push(props.actionsClassName);

  const readOnly = props.mode.type === 'view';
  const disabled = readOnly || formik.isSubmitting || !formik.isValid;

  useImperativeHandle(ref, () => ({
    reset: () => formik.resetForm(),
    submit: () => formik.submitForm(),
  }));

  useEffect(() => {
    if (props.onStateUpdate)
      props.onStateUpdate({
        values: formik.values,
        disabled,
      });
  }, [formik.values, disabled]);

  const venueTemplateIds = props.venueTemplates.filter(vt => !vt.archived).map(vt => vt._id);
  const vtMap = mapBy(props.venueTemplates, '_id');

  const isOffer = props.template.type === 'offer';
  const [selectedLang, setSelectedLang] = useState(defaultLanguage.key);

  const contentTemplateTranslationsMap = mapBy(props.contentTemplateTranslations, 'refers');
  const cntTmpl =
    (selectedLang != defaultLanguage.key
      ? translateBareContentTemplate(
          props.template,
          contentTemplateTranslationsMap[props.template._id],
          selectedLang
        )
      : props.template) || props.template;

  async function onFormSubmit(values: ContentFormikForm) {
    const { startTime, endTime, ...rest } = values;
    const duration = calculateTimeDifference(endTime, startTime);

    const newContent: Content = {
      ...(props.mode.edit || {
        _id: new ObjectID().toHexString(),
        template: props.template,
        day: props.plan.day,
        callToActions: { syncWithTemplate: true, values: [] },
      }),
      ...rest,
      startTime,
      duration,
    };

    if (props.onSubmit) props.onSubmit(newContent);
  }

  const formActions = (
    <div className="flex items-center">
      <div></div>
      <div className="ml-auto" />
      <BubbleButton disabled={disabled} variant="navy" type="reset" inverse icon="rotate-left">
        Reset
      </BubbleButton>
      <BubbleButton variant="navy" type="submit" disabled={disabled} icon="floppy-disk">
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem
    ? createPortal(formActions, props.actionsElem)
    : formActions;

  return (
    <FormikProvider value={formik}>
      <div className="flex items-center -mx-6 mb-5">
        <BubbleButton
          className={`h-full ${activeTab === 'basic' ? '' : 'font-normal-i'}`}
          appearance="squared"
          variant={activeTab === 'basic' ? 'navy' : 'smoke'}
          block
          onClick={() => setActiveTab('basic')}
        >
          Instance Information
        </BubbleButton>
        <BubbleButton
          className={`h-full ${activeTab === 'mergedVars' ? '' : 'font-normal-i'}`}
          appearance="squared"
          variant={activeTab === 'mergedVars' ? 'navy' : 'smoke'}
          block
          onClick={() => setActiveTab('mergedVars')}
        >
          Merged Variables
        </BubbleButton>
      </div>
      <Form onSubmit={handleSubmit}>
        {activeTab === 'basic' ? (
          <div className="grid grid-cols-6 gap-4">
            <BubbleFormGroup
              className="col-span-3"
              label="Start Time"
              asterisk
              error={errors.startTime && touched.startTime && errors.startTime}
            >
              <BubbleInput
                block
                type="time"
                name="startTime"
                readOnly={readOnly}
                onBlur={handleBlur}
                value={values.startTime}
                onValue={value => setFieldValue('startTime', value)}
              />
            </BubbleFormGroup>
            <BubbleFormGroup
              className="col-span-3"
              label="End Time"
              asterisk
              error={errors.endTime && touched.endTime && errors.endTime}
            >
              <BubbleInput
                block
                type="time"
                name="endTime"
                readOnly={readOnly}
                onBlur={handleBlur}
                value={values.endTime}
                onValue={value => setFieldValue('endTime', value)}
              />
            </BubbleFormGroup>
            {isOffer && (
              <BubbleFormGroup
                className="col-span-6"
                label="Venue"
                error={errors.venue && touched.venue && errors.venue}
              >
                <BubbleSelect
                  block
                  allowUndefined
                  labelExtractor={v => vtMap[v].name}
                  options={venueTemplateIds}
                  name="venue"
                  readOnly={readOnly}
                  onValue={value => setFieldValue('venue', value)}
                  onBlur={handleBlur}
                  value={values.venue}
                />
              </BubbleFormGroup>
            )}

            <div className="col-span-6">
              <CallToActionsManagement
                sourceCtas={values.callToActions}
                parentCtas={props.template.callToActions}
                onUpdate={(newCta: CallToActionsInstance) => {
                  setFieldValue('callToActions', newCta);
                }}
              />
            </div>
            <hr className="col-span-6 my-1" />
            <div className="col-span-3">
              <BubbleFormGroup label="Endpoints">
                <McmVisibilityTable
                  isTemplate={false}
                  sourceMcm={values.mcm}
                  parentMcm={props.template.mcm}
                  onUpdateMcm={(newMcm: Mcm) => setFieldValue('mcm', newMcm)}
                />
              </BubbleFormGroup>
            </div>
            <div className="col-span-3">
              <BubbleFormGroup label="Audience">
                <AudienceVisibilityTable
                  isTemplate={false}
                  sourceAud={values.audience}
                  parentAud={props.template.audience}
                  onUpdateAudience={(newAud: Audience) => setFieldValue('audience', newAud)}
                />
              </BubbleFormGroup>
            </div>
            {props.mode.type !== 'view' && (
              <div className={actionClasses.join(' ')}>{portalButtons}</div>
            )}
          </div>
        ) : (
          <div>
            {mergedVarKeys.length > 0 && isNotBasicContentTemplate(cntTmpl) ? (
              <div className="h-full flex items-start bg-eggshell -mx-6">
                <div className="sticky top-0 px-4 flex-shrink-0">
                  <div className="grid grid-cols-1">
                    <BubbleButton
                      variant="navy"
                      className="px-4-i"
                      inverse={selectedLang != defaultLanguage.key}
                      onClick={() => setSelectedLang(defaultLanguage.key)}
                    >
                      {defaultLanguage.englishLabel}
                    </BubbleButton>
                    {languages.map(l => (
                      <BubbleButton
                        key={l.key}
                        className="px-5-i"
                        onClick={() => setSelectedLang(l.key)}
                        variant="navy"
                        inverse={selectedLang != l.key}
                      >
                        {l.englishLabel}
                      </BubbleButton>
                    ))}
                  </div>
                </div>
                <div className="flex-grow grid gap-4 grid-cols-1 pr-4 pb-4 pt-0">
                  <div className="bg-white px-6 py-4 border grid gap-4 grid-cols-1">
                    <BubbleFormGroup label="Title">{cntTmpl.name}</BubbleFormGroup>
                    <BubbleFormGroup label="Description">
                      <div
                        dangerouslySetInnerHTML={{
                          __html: cntTmpl.description,
                        }}
                      />
                    </BubbleFormGroup>
                  </div>
                  {mergedVarKeys.map(mv => (
                    <div key={mv}>
                      {selectedLang === defaultLanguage.key ? (
                        <BubbleFormGroup label={mv}>
                          <div className="flex items-start">
                            <BubbleInput
                              type="checkbox"
                              className="mr-2"
                              inputClassName="bg-white"
                              checked={values.mergedVars.replacements[mv].enabled}
                              onValue={checked =>
                                setFieldValue('mergedVars', {
                                  ...values.mergedVars,
                                  replacements: {
                                    ...values.mergedVars.replacements,
                                    [mv]: {
                                      ...values.mergedVars.replacements[mv],
                                      enabled: checked,
                                    },
                                  },
                                })
                              }
                            />
                            <BubbleTextArea
                              block
                              className="flex-grow"
                              value={values.mergedVars.replacements[mv].value}
                              onClick={() =>
                                setFieldValue('mergedVars', {
                                  ...values.mergedVars,
                                  replacements: {
                                    ...values.mergedVars.replacements,
                                    [mv]: {
                                      ...values.mergedVars.replacements[mv],
                                      enabled: true,
                                    },
                                  },
                                })
                              }
                              onValue={val =>
                                setFieldValue('mergedVars', {
                                  ...values.mergedVars,
                                  replacements: {
                                    ...values.mergedVars.replacements,
                                    [mv]: {
                                      ...values.mergedVars.replacements[mv],
                                      value: val,
                                    },
                                  },
                                })
                              }
                            />
                          </div>
                        </BubbleFormGroup>
                      ) : (
                        <BubbleFormGroup label={mv + '*'}>
                          <div className="flex items-start">
                            <BubbleInput
                              type="checkbox"
                              className="mr-2"
                              inputClassName="bg-white"
                              checked={values.mergedVars.translations[selectedLang][mv].enabled}
                              onValue={checked =>
                                setFieldValue('mergedVars', {
                                  ...values.mergedVars,
                                  translations: {
                                    ...values.mergedVars.translations,
                                    [selectedLang]: {
                                      ...values.mergedVars.translations[selectedLang],
                                      [mv]: {
                                        ...values.mergedVars.translations[selectedLang][mv],
                                        enabled: checked,
                                      },
                                    },
                                  },
                                })
                              }
                            />
                            <BubbleTextArea
                              block
                              className="flex-grow"
                              onClick={() =>
                                setFieldValue('mergedVars', {
                                  ...values.mergedVars,
                                  translations: {
                                    ...values.mergedVars.translations,
                                    [selectedLang]: {
                                      ...values.mergedVars.translations[selectedLang],
                                      [mv]: {
                                        ...values.mergedVars.translations[selectedLang][mv],
                                        enabled: true,
                                      },
                                    },
                                  },
                                })
                              }
                              value={values.mergedVars.translations[selectedLang][mv].value}
                              onValue={val =>
                                setFieldValue('mergedVars', {
                                  ...values.mergedVars,
                                  translations: {
                                    ...values.mergedVars.translations,
                                    [selectedLang]: {
                                      ...values.mergedVars.translations[selectedLang],
                                      [mv]: {
                                        ...values.mergedVars.translations[selectedLang][mv],
                                        value: val,
                                      },
                                    },
                                  },
                                })
                              }
                            />
                          </div>
                        </BubbleFormGroup>
                      )}
                    </div>
                  ))}
                  {selectedLang !== defaultLanguage.key && (
                    <div className="italic text-xs text-slate sticky bottom-0">
                      *Defaults to {defaultLanguage.englishLabel} if not enabled
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <BubbleAlert variant="ocean">
                No merged variables found in title or description of this event
              </BubbleAlert>
            )}
          </div>
        )}
      </Form>
    </FormikProvider>
  );
}

export default forwardRef<ContentFormikRef, ContentFormikProps>(ContentFormik);
